/**
 * bpsessions.ts — draw the BP channel's Asia-sweep session objects. Compute nothing.
 *
 * Card `t_5ee634f0`. Every number arrives from `/api/overlays/{a}/{tf}/bp_sessions`, which is
 * `ict.bp_session` (the rules, their timecodes and the choices the author leaves open live in that
 * module's docstring; `choices` serves them verbatim). This file maps rows to shapes and decides no
 * rule — `overlays.ts`' standing contract: geometry in, pixels out.
 *
 *   pre-windows  one band per CLOSED hour before Frankfurt (01-02 ET) and before New York (07-08
 *                ET), first..last bar at its high and low. `exactSpan`: the width is the hour.
 *   50% line     a 1px band at (AH + AL) / 2, from the sweep bar to where the day resolved (its
 *                `worked` touch or the 11:00 close), or to the right edge while still open.
 *   V3a stop     the swept boundary ± 0.5 × the Asia range [F 018 270], over the same span.
 *   V2 stop      the Frankfurt extreme on the swept side (020 15:38), Frankfurt sweeps only, from
 *                the extreme bar; its 02:33 macro class is printed only on a GBP asset, the one
 *                the rule is stated for (020 11:02) — elsewhere the row says it is not applied.
 *   markers      `S·FR` / `S·LN` / `S·NY` on the sweep bar (the session it was taken in), `50` on
 *                the first later bar to reach the line, `W` on the target touch, `P` / `F` on the
 *                bar whose close (11:00) made partial / failed known.
 *
 * NOT A SIGNAL: both stops are TEACHING versions (018, 020), not his live 1R-mirror rule.
 */
import type { SeriesMarker, Time, UTCTimestamp } from 'lightweight-charts';
import type { ZoneBand } from './overlays';

export type BpSessionName = 'frankfurt' | 'london' | 'ny';

export type BpBox = {
  session: 'pre_frankfurt' | 'pre_ny';
  trading_day: string;
  time: UTCTimestamp;
  end_time: UTCTimestamp;
  high: number;
  low: number;
  n_bars: number;
};

export type BpSweep = {
  trading_day: string;
  time: UTCTimestamp;
  /** Where the day resolved; null while open; the sweep's own bar for a `both` bar. */
  end_time: UTCTimestamp | null;
  side: 'high' | 'low' | 'both';
  session: BpSessionName;
  asia_high: number;
  asia_low: number;
  asia_mid: number;
  entry: number | null;
  target: number | null;
  stop_v3: number | null;
};

export type BpFrankfurtStop = {
  trading_day: string;
  time: UTCTimestamp;
  end_time: UTCTimestamp | null;
  side: 'high' | 'low';
  stop_v2: number;
  macro_0233: 'after' | 'before' | 'straddles';
};

export type BpMidTouch = { trading_day: string; time: UTCTimestamp; price: number };

export type BpOutcome = {
  trading_day: string;
  kind: 'worked' | 'partial' | 'failed';
  time: UTCTimestamp;
};

export type BpMiss = { trading_day: string; kind: 'no_sweep'; time: UTCTimestamp };

export type BpSessionsPayload = {
  component: 'bp_sessions';
  source: string;
  unsupported_tf: string | null;
  macro_rule_applies: boolean;
  boxes: BpBox[];
  sweeps: BpSweep[];
  frankfurt_stops: BpFrankfurtStop[];
  mid_touches: BpMidTouch[];
  outcomes: BpOutcome[];
  misses: BpMiss[];
};

const BOX_STYLE: Record<BpBox['session'], { fill: string; border: string; label: string }> = {
  pre_frankfurt: { fill: 'rgba(0, 188, 212, 0.08)', border: 'rgba(0, 188, 212, 0.65)', label: 'pre-FR' },
  pre_ny: { fill: 'rgba(255, 87, 34, 0.07)', border: 'rgba(255, 87, 34, 0.60)', label: 'pre-NY' },
};

export const MID_COLOR = 'rgba(0, 188, 212, 0.95)';
export const STOP_COLOR = 'rgba(244, 67, 54, 0.85)';
const SESSION_TAG: Record<BpSessionName, string> = { frankfurt: 'FR', london: 'LN', ny: 'NY' };
const OUTCOME_TEXT: Record<BpOutcome['kind'], string> = { worked: 'W', partial: 'P', failed: 'F' };

/** A shape that began before the pane's first bar starts at that bar: `RangePrimitive` draws
 *  nothing for a band anchored before the series (the `sessions.ts` clamp, quant r1 there). */
const clamp = (t: UTCTimestamp, first: number | null): UTCTimestamp =>
  (first != null && t < first ? first : t) as UTCTimestamp;

const line = (price: number, from: UTCTimestamp, to: UTCTimestamp | null, color: string,
  label: string, first: number | null): ZoneBand => ({
  lo: price, hi: price, fromTime: clamp(from, first), toTime: to, fill: color, border: color,
  label, exactSpan: true,
});

export function bpBoxBands(p: BpSessionsPayload, windowFirstTime: number | null = null): ZoneBand[] {
  return p.boxes.map((b) => ({
    lo: b.low,
    hi: b.high,
    fromTime: clamp(b.time, windowFirstTime),
    toTime: b.end_time,
    fill: BOX_STYLE[b.session].fill,
    border: BOX_STYLE[b.session].border,
    label: BOX_STYLE[b.session].label,
    exactSpan: true,
  }));
}

/** 50% line and V3a stop per one-sided sweep; V2 stop per Frankfurt sweep. A `both` bar names
 *  no side, so it has neither. */
export function bpLineBands(p: BpSessionsPayload, windowFirstTime: number | null = null): ZoneBand[] {
  const out: ZoneBand[] = [];
  for (const s of p.sweeps) {
    if (s.side === 'both' || s.stop_v3 == null) continue;
    out.push(line(s.asia_mid, s.time, s.end_time, MID_COLOR, 'Asia 50%', windowFirstTime));
    out.push(line(s.stop_v3, s.time, s.end_time, STOP_COLOR, 'V3a stop 0.5×Asia', windowFirstTime));
  }
  for (const f of p.frankfurt_stops) {
    const macro = p.macro_rule_applies ? ` · 02:33 ${f.macro_0233}` : '';
    out.push(line(f.stop_v2, f.time, f.end_time, STOP_COLOR, `V2 stop FR extreme${macro}`,
      windowFirstTime));
  }
  return out;
}

/** A marker is NOT drawn when its bar is left of the pane (no bar to put it on). */
export function bpMarkers(p: BpSessionsPayload, windowFirstTime: number | null = null): SeriesMarker<Time>[] {
  const out: SeriesMarker<Time>[] = [];
  const onPane = (t: UTCTimestamp) => windowFirstTime == null || t >= windowFirstTime;
  const side = new Map(p.sweeps.map((s) => [s.trading_day, s.side]));
  // The move after a HIGH sweep is down, so its 50 / W / P / F sit under the bar, and vice versa.
  const after = (day: string) => (side.get(day) === 'high' ? 'belowBar' : 'aboveBar');
  for (const s of p.sweeps) {
    if (!onPane(s.time)) continue;
    out.push({
      time: s.time,
      position: s.side === 'low' ? 'belowBar' : 'aboveBar',
      color: MID_COLOR,
      shape: s.side === 'low' ? 'arrowUp' : s.side === 'high' ? 'arrowDown' : 'square',
      text: `S·${SESSION_TAG[s.session]}${s.side === 'both' ? ' both' : ''}`,
    });
  }
  for (const m of p.mid_touches) {
    if (!onPane(m.time)) continue;
    out.push({ time: m.time, position: after(m.trading_day), color: MID_COLOR, shape: 'circle', text: '50' });
  }
  for (const o of p.outcomes) {
    if (!onPane(o.time)) continue;
    out.push({
      time: o.time,
      position: after(o.trading_day),
      color: o.kind === 'failed' ? STOP_COLOR : MID_COLOR,
      shape: 'circle',
      text: OUTCOME_TEXT[o.kind],
    });
  }
  return out.sort((a, b) => (a.time as number) - (b.time as number));
}

export function bpSummary(p: BpSessionsPayload): [string, string][] {
  if (p.unsupported_tf) return [['BP sessions', p.unsupported_tf]];
  const n = (k: BpOutcome['kind']) => p.outcomes.filter((o) => o.kind === k).length;
  const tag = (s: BpSessionName) => p.sweeps.filter((x) => x.session === s).length;
  return [
    ['BP sessions', `${p.boxes.length} pre-window boxes (pre-FR 01-02 / pre-NY 07-08 ET)`],
    ['what this is', 'BP Asia sweep 02-10 ET, closed bars only — NOT an entry signal'],
    ['Asia swept (FR / LN / NY)', `${p.sweeps.length} (${tag('frankfurt')} / ${tag('london')} / ${tag('ny')})`],
    ['worked / partial / failed by 11:00', `${n('worked')} / ${n('partial')} / ${n('failed')}`],
    ['no sweep 02-10 ET', String(p.misses.filter((m) => m.kind === 'no_sweep').length)],
    ['02:33 macro (V2 extreme)', p.macro_rule_applies ? 'applied (GBP, 020 11:02)'
      : 'not applied — stated for GBP only'],
  ];
}

/** ONE CALL for every shape and the rows describing them (`sessionsDrawing`'s shape). */
export function bpSessionsDrawing(p: BpSessionsPayload, windowFirstTime: number | null = null): {
  bands: ZoneBand[];
  markers: SeriesMarker<Time>[];
  rows: [string, string][];
} {
  return {
    bands: [...bpBoxBands(p, windowFirstTime), ...bpLineBands(p, windowFirstTime)],
    markers: bpMarkers(p, windowFirstTime),
    rows: bpSummary(p),
  };
}
